import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { 
  GitBranch, 
  ArrowRight, 
  ArrowDown, 
  RefreshCw,
  Download,
  Zap,
  Building,
  Target,
  BarChart3,
  Layers,
  Home,
  Calculator,
  Eye,
  AlertCircle
} from "lucide-react";
import { Alert, AlertDescription } from "./ui/alert";
import { Separator } from "./ui/separator";

const AllocationFlowVisualizer = ({ 
  calculationId,
  businessUnits = [],
  threeFactorFormula = null,
  intermediatePools = [],
  squareFootageResults = [],
  customPoolResults = [],
  allocationResults = []
}) => {
  const [flowData, setFlowData] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [viewMode, setViewMode] = useState("diagram"); // "diagram" or "list"

  useEffect(() => {
    generateFlowData();
  }, [businessUnits, threeFactorFormula, intermediatePools, squareFootageResults, customPoolResults, allocationResults]);

  const generateFlowData = () => {
    const flow = {
      nodes: [],
      edges: [],
      summary: {
        total_nodes: 0,
        total_flow_amount: 0,
        allocation_methods: [],
        pool_types: []
      }
    };

    // Add source nodes (Business Units)
    businessUnits.forEach((unit, index) => {
      flow.nodes.push({
        id: `bu_${unit.id}`,
        type: "business_unit",
        label: unit.unit_name,
        data: {
          unit_code: unit.unit_code,
          headcount: unit.headcount,
          annual_payroll: unit.annual_payroll,
          annual_revenue: unit.annual_revenue,
          description: unit.description
        },
        position: { x: 50, y: 50 + (index * 120) },
        style: { backgroundColor: "#dbeafe", borderColor: "#3b82f6" }
      });
    });

    // Add Square Footage Location nodes
    squareFootageResults.forEach((result, index) => {
      flow.nodes.push({
        id: `sf_${result.location_id}`,
        type: "square_footage",
        label: result.location_name,
        data: {
          total_square_footage: result.total_square_footage,
          total_allocated_amount: result.total_allocated_amount,
          allocation_per_sqft: result.allocation_per_sqft,
          rent_allocation: result.rent_allocation,
          utilities_allocation: result.utilities_allocation,
          other_occupancy_allocation: result.other_occupancy_allocation
        },
        position: { x: 300, y: 50 + (index * 120) },
        style: { backgroundColor: "#dcfce7", borderColor: "#16a34a" }
      });

      // Add edges from square footage to pools
      Object.entries(result.rent_allocation || {}).forEach(([pool, amount]) => {
        if (amount > 0) {
          flow.edges.push({
            id: `sf_${result.location_id}_to_${pool}_rent`,
            source: `sf_${result.location_id}`,
            target: `pool_${pool}`,
            label: `Rent: $${amount.toLocaleString()}`,
            data: { amount, category: "rent" }
          });
        }
      });

      Object.entries(result.utilities_allocation || {}).forEach(([pool, amount]) => {
        if (amount > 0) {
          flow.edges.push({
            id: `sf_${result.location_id}_to_${pool}_utilities`,
            source: `sf_${result.location_id}`,
            target: `pool_${pool}`,
            label: `Utilities: $${amount.toLocaleString()}`,
            data: { amount, category: "utilities" }
          });
        }
      });
    });

    // Add Custom Pool nodes
    customPoolResults.forEach((result, index) => {
      flow.nodes.push({
        id: `cp_${result.custom_pool_id}`,
        type: "custom_pool",
        label: result.custom_pool_name,
        data: {
          source_pool_costs: result.source_pool_costs,
          allocation_method: result.allocation_method,
          allocated_amounts: result.allocated_amounts,
          allocation_percentages: result.allocation_percentages,
          target_pools: result.target_pools
        },
        position: { x: 550, y: 50 + (index * 120) },
        style: { backgroundColor: "#fef3c7", borderColor: "#f59e0b" }
      });

      // Add edges from custom pools to target pools
      Object.entries(result.allocated_amounts || {}).forEach(([pool, amount]) => {
        if (amount > 0) {
          flow.edges.push({
            id: `cp_${result.custom_pool_id}_to_${pool}`,
            source: `cp_${result.custom_pool_id}`,
            target: `pool_${pool}`,
            label: `$${amount.toLocaleString()} (${result.allocation_percentages[pool]?.toFixed(1)}%)`,
            data: { amount, percentage: result.allocation_percentages[pool] }
          });
        }
      });
    });

    // Add standard pool nodes (G&A, OH, Fringe)
    const standardPools = ["G&A", "OH", "Fringe"];
    standardPools.forEach((pool, index) => {
      flow.nodes.push({
        id: `pool_${pool}`,
        type: "standard_pool",
        label: `${pool} Pool`,
        data: { pool_type: pool },
        position: { x: 800, y: 100 + (index * 150) },
        style: { backgroundColor: "#f3e8ff", borderColor: "#8b5cf6" }
      });
    });

    // Add Three-Factor Formula flows if available
    if (threeFactorFormula && allocationResults.length > 0) {
      allocationResults.forEach((profileResult) => {
        if (profileResult.results?.profile_type === "three_factor" && 
            profileResult.results.calculation_results?.allocation_breakdown) {
          
          profileResult.results.calculation_results.allocation_breakdown.forEach((allocation) => {
            const businessUnit = businessUnits.find(bu => bu.unit_name === allocation.business_unit_name);
            if (businessUnit) {
              flow.edges.push({
                id: `bu_${businessUnit.id}_three_factor`,
                source: `bu_${businessUnit.id}`,
                target: `pool_G&A`, // Three-factor typically goes to G&A
                label: `Three-Factor: $${allocation.allocated_amount.toLocaleString()} (${(allocation.weighted_allocation * 100).toFixed(1)}%)`,
                data: { 
                  amount: allocation.allocated_amount,
                  percentage: allocation.weighted_allocation * 100,
                  method: "three_factor",
                  headcount_factor: allocation.headcount_factor,
                  payroll_factor: allocation.payroll_factor,
                  revenue_factor: allocation.revenue_factor
                }
              });
            }
          });
        }
      });
    }

    // Calculate summary statistics
    flow.summary.total_nodes = flow.nodes.length;
    flow.summary.total_flow_amount = flow.edges.reduce((sum, edge) => 
      sum + (edge.data?.amount || 0), 0
    );
    flow.summary.allocation_methods = [...new Set([
      ...squareFootageResults.map(() => "square_footage"),
      ...customPoolResults.map(r => r.allocation_method),
      ...(threeFactorFormula ? ["three_factor"] : [])
    ])];
    flow.summary.pool_types = [...new Set(flow.nodes.map(n => n.type))];

    setFlowData(flow);
  };

  const handleNodeSelect = (node) => {
    setSelectedNode(selectedNode?.id === node.id ? null : node);
  };

  const exportFlowDiagram = () => {
    if (!flowData) {
      alert("No flow data available to export");
      return;
    }

    // Create a detailed flow report
    const report = {
      title: "Allocation Flow Diagram Export",
      calculation_id: calculationId,
      generated_at: new Date().toISOString(),
      summary: flowData.summary,
      nodes: flowData.nodes.map(node => ({
        id: node.id,
        type: node.type,
        label: node.label,
        data: node.data
      })),
      edges: flowData.edges.map(edge => ({
        id: edge.id,
        source: edge.source,
        target: edge.target,
        label: edge.label,
        data: edge.data
      }))
    };

    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Allocation_Flow_${calculationId.slice(0, 8)}_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const renderFlowNode = (node) => {
    const getNodeIcon = (type) => {
      switch (type) {
        case "business_unit": return <Building className="h-4 w-4" />;
        case "square_footage": return <Home className="h-4 w-4" />;
        case "custom_pool": return <Layers className="h-4 w-4" />;
        case "standard_pool": return <Target className="h-4 w-4" />;
        default: return <Calculator className="h-4 w-4" />;
      }
    };

    const getNodeColor = (type) => {
      switch (type) {
        case "business_unit": return "bg-blue-50 border-blue-200 hover:bg-blue-100";
        case "square_footage": return "bg-green-50 border-green-200 hover:bg-green-100";
        case "custom_pool": return "bg-yellow-50 border-yellow-200 hover:bg-yellow-100";
        case "standard_pool": return "bg-blue-50 border-blue-200 hover:bg-blue-100";
        default: return "bg-slate-50 border-slate-200 hover:bg-slate-100";
      }
    };

    const isSelected = selectedNode?.id === node.id;

    return (
      <div
        key={node.id}
        className={`
          p-3 border-2 rounded-lg cursor-pointer transition-all
          ${getNodeColor(node.type)}
          ${isSelected ? 'ring-2 ring-indigo-500 border-indigo-300' : ''}
        `}
        onClick={() => handleNodeSelect(node)}
      >
        <div className="flex items-center space-x-2 mb-2">
          {getNodeIcon(node.type)}
          <span className="font-medium text-sm">{node.label}</span>
        </div>
        <div className="text-xs text-slate-600">
          {node.type === "business_unit" && (
            <div>
              <div>Code: {node.data.unit_code}</div>
              <div>Headcount: {node.data.headcount || 0}</div>
            </div>
          )}
          {node.type === "square_footage" && (
            <div>
              <div>Sq Ft: {node.data.total_square_footage?.toLocaleString()}</div>
              <div>Total: ${node.data.total_allocated_amount?.toLocaleString()}</div>
            </div>
          )}
          {node.type === "custom_pool" && (
            <div>
              <div>Method: {node.data.allocation_method}</div>
              <div>Source: ${node.data.source_pool_costs?.toLocaleString()}</div>
            </div>
          )}
          {node.type === "standard_pool" && (
            <div>{node.data.pool_type} Pool</div>
          )}
        </div>
      </div>
    );
  };

  const renderSelectedNodeDetails = () => {
    if (!selectedNode) return null;

    return (
      <Card className="mt-4">
        <CardHeader>
          <CardTitle className="text-base flex items-center space-x-2">
            <Eye className="h-4 w-4" />
            <span>{selectedNode.label} Details</span>
            <Badge variant="outline">{selectedNode.type.replace('_', ' ')}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {selectedNode.type === "business_unit" && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium">Unit Code:</span>
                  <div>{selectedNode.data.unit_code}</div>
                </div>
                <div>
                  <span className="font-medium">Headcount:</span>
                  <div>{selectedNode.data.headcount || "N/A"}</div>
                </div>
                <div>
                  <span className="font-medium">Annual Payroll:</span>
                  <div>${selectedNode.data.annual_payroll?.toLocaleString() || "N/A"}</div>
                </div>
                <div>
                  <span className="font-medium">Annual Revenue:</span>
                  <div>${selectedNode.data.annual_revenue?.toLocaleString() || "N/A"}</div>
                </div>
                {selectedNode.data.description && (
                  <div className="col-span-2">
                    <span className="font-medium">Description:</span>
                    <div>{selectedNode.data.description}</div>
                  </div>
                )}
              </div>
            )}

            {selectedNode.type === "square_footage" && (
              <div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm mb-4">
                  <div>
                    <span className="font-medium">Total Square Footage:</span>
                    <div>{selectedNode.data.total_square_footage?.toLocaleString()}</div>
                  </div>
                  <div>
                    <span className="font-medium">Total Allocated:</span>
                    <div>${selectedNode.data.total_allocated_amount?.toLocaleString()}</div>
                  </div>
                  <div>
                    <span className="font-medium">Per Sq Ft:</span>
                    <div>${selectedNode.data.allocation_per_sqft?.toFixed(2)}</div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  {selectedNode.data.rent_allocation && Object.keys(selectedNode.data.rent_allocation).length > 0 && (
                    <div>
                      <span className="font-medium">Rent Allocation:</span>
                      {Object.entries(selectedNode.data.rent_allocation).map(([pool, amount]) => (
                        <div key={pool} className="flex justify-between text-sm ml-4">
                          <span>{pool}:</span>
                          <span>${amount?.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {selectedNode.data.utilities_allocation && Object.keys(selectedNode.data.utilities_allocation).length > 0 && (
                    <div>
                      <span className="font-medium">Utilities Allocation:</span>
                      {Object.entries(selectedNode.data.utilities_allocation).map(([pool, amount]) => (
                        <div key={pool} className="flex justify-between text-sm ml-4">
                          <span>{pool}:</span>
                          <span>${amount?.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {selectedNode.type === "custom_pool" && (
              <div className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium">Allocation Method:</span>
                    <div className="capitalize">{selectedNode.data.allocation_method}</div>
                  </div>
                  <div>
                    <span className="font-medium">Source Pool Costs:</span>
                    <div>${selectedNode.data.source_pool_costs?.toLocaleString()}</div>
                  </div>
                </div>

                {selectedNode.data.allocated_amounts && (
                  <div>
                    <span className="font-medium">Allocated Amounts:</span>
                    {Object.entries(selectedNode.data.allocated_amounts).map(([pool, amount]) => (
                      <div key={pool} className="flex justify-between text-sm ml-4">
                        <span>{pool}:</span>
                        <span>${amount?.toLocaleString()} ({selectedNode.data.allocation_percentages?.[pool]?.toFixed(1)}%)</span>
                      </div>
                    ))}
                  </div>
                )}

                {selectedNode.data.target_pools && (
                  <div>
                    <span className="font-medium">Target Pools:</span>
                    <div className="flex flex-wrap gap-1 ml-4 mt-1">
                      {selectedNode.data.target_pools.map((pool) => (
                        <Badge key={pool} variant="outline" className="text-xs">{pool}</Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {selectedNode.type === "standard_pool" && (
              <div>
                <div className="text-sm">
                  <span className="font-medium">Pool Type:</span>
                  <div>{selectedNode.data.pool_type}</div>
                </div>
                
                {/* Calculate incoming flows for this pool */}
                {(() => {
                  const incomingFlows = flowData?.edges.filter(edge => 
                    edge.target === selectedNode.id
                  ) || [];
                  
                  if (incomingFlows.length > 0) {
                    const totalIncoming = incomingFlows.reduce((sum, edge) => 
                      sum + (edge.data?.amount || 0), 0
                    );
                    
                    return (
                      <div className="mt-3">
                        <span className="font-medium">Incoming Allocations:</span>
                        <div className="ml-4 space-y-1">
                          <div className="text-sm font-medium">
                            Total: ${totalIncoming.toLocaleString()}
                          </div>
                          {incomingFlows.map((flow, idx) => (
                            <div key={idx} className="text-xs text-slate-600 flex justify-between">
                              <span>{flow.id.replace(/_/g, ' ')}</span>
                              <span>${flow.data?.amount?.toLocaleString()}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  if (!flowData) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2">Generating allocation flow visualization...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header and Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <GitBranch className="h-5 w-5 text-indigo-600" />
          <h3 className="text-lg font-semibold">Allocation Flow Visualizer</h3>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setViewMode(viewMode === "diagram" ? "list" : "diagram")}
          >
            <BarChart3 className="h-4 w-4 mr-2" />
            {viewMode === "diagram" ? "List View" : "Diagram View"}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => generateFlowData()}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={exportFlowDiagram}
          >
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Summary Statistics */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Zap className="h-5 w-5 text-green-600" />
            <span>Flow Summary</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="text-sm font-medium text-blue-900">Total Nodes</div>
              <div className="text-2xl font-bold text-blue-600">{flowData.summary.total_nodes}</div>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="text-sm font-medium text-green-900">Total Flow Amount</div>
              <div className="text-2xl font-bold text-green-600">
                ${flowData.summary.total_flow_amount.toLocaleString()}
              </div>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="text-sm font-medium text-blue-900">Allocation Methods</div>
              <div className="text-lg font-bold text-blue-600">
                {flowData.summary.allocation_methods.length}
              </div>
              <div className="text-xs text-blue-500">
                {flowData.summary.allocation_methods.join(", ")}
              </div>
            </div>
            <div className="bg-orange-50 p-4 rounded-lg">
              <div className="text-sm font-medium text-orange-900">Pool Types</div>
              <div className="text-lg font-bold text-orange-600">
                {flowData.summary.pool_types.length}
              </div>
              <div className="text-xs text-orange-500">
                {flowData.summary.pool_types.join(", ")}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Flow Visualization */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <GitBranch className="h-5 w-5 text-slate-600" />
            <span>Allocation Flow {viewMode === "diagram" ? "Diagram" : "List"}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {viewMode === "diagram" ? (
            <div className="space-y-6">
              {/* Source Nodes */}
              <div>
                <h4 className="font-semibold text-slate-700 mb-3 flex items-center space-x-2">
                  <ArrowRight className="h-4 w-4" />
                  <span>Sources</span>
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {flowData.nodes
                    .filter(node => ["business_unit", "square_footage"].includes(node.type))
                    .map(renderFlowNode)}
                </div>
              </div>

              <div className="flex items-center justify-center">
                <ArrowDown className="h-8 w-8 text-slate-400" />
              </div>

              {/* Intermediate Nodes */}
              <div>
                <h4 className="font-semibold text-slate-700 mb-3 flex items-center space-x-2">
                  <ArrowRight className="h-4 w-4" />
                  <span>Intermediate Pools</span>
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {flowData.nodes
                    .filter(node => node.type === "custom_pool")
                    .map(renderFlowNode)}
                </div>
              </div>

              <div className="flex items-center justify-center">
                <ArrowDown className="h-8 w-8 text-slate-400" />
              </div>

              {/* Target Nodes */}
              <div>
                <h4 className="font-semibold text-slate-700 mb-3 flex items-center space-x-2">
                  <ArrowRight className="h-4 w-4" />
                  <span>Final Pools</span>
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {flowData.nodes
                    .filter(node => node.type === "standard_pool")
                    .map(renderFlowNode)}
                </div>
              </div>
            </div>
          ) : (
            /* List View */
            <div className="space-y-4">
              {flowData.edges.length > 0 ? (
                <div className="divide-y">
                  {flowData.edges.map((edge, index) => {
                    const sourceNode = flowData.nodes.find(n => n.id === edge.source);
                    const targetNode = flowData.nodes.find(n => n.id === edge.target);
                    
                    return (
                      <div key={edge.id} className="py-3 flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <Badge variant="outline" className="text-xs">{index + 1}</Badge>
                          <div className="flex items-center space-x-2">
                            <span className="font-medium text-sm">{sourceNode?.label}</span>
                            <ArrowRight className="h-4 w-4 text-slate-400" />
                            <span className="font-medium text-sm">{targetNode?.label}</span>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Badge variant="secondary" className="text-xs">
                            {edge.data?.method || "allocation"}
                          </Badge>
                          <div className="text-sm font-medium">
                            ${edge.data?.amount?.toLocaleString() || "0"}
                            {edge.data?.percentage && (
                              <span className="text-slate-500 ml-1">
                                ({edge.data.percentage.toFixed(1)}%)
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <GitBranch className="h-16 w-16 mx-auto mb-4 text-slate-300" />
                  <p>No allocation flows to display</p>
                  <p className="text-sm mt-2">
                    Configure square footage locations or custom pools to see allocation flows
                  </p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Selected Node Details */}
      {renderSelectedNodeDetails()}

      {/* Information Panel */}
      <div className="p-4 bg-slate-50 rounded-lg">
        <div className="flex items-center space-x-2 text-sm text-slate-600">
          <AlertCircle className="h-4 w-4" />
          <div>
            <p className="font-medium">Allocation Flow Visualizer Information</p>
            <ul className="text-xs mt-1 space-y-1">
              <li>• Interactive diagram showing the complete allocation flow from sources to final pools</li>
              <li>• Click on any node to see detailed information about that allocation step</li>
              <li>• Blue nodes represent business units, green nodes are square footage locations</li>
              <li>• Yellow nodes are custom pools, blue nodes are standard pools (G&A, OH, Fringe)</li>
              <li>• Export functionality provides complete allocation flow data for audit purposes</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AllocationFlowVisualizer;